/* eslint-disable @typescript-eslint/no-explicit-any */
// Maps sign-in failures (HR Mobile API and Microsoft Entra ID) to one code for AccessErrorPage.

export type AccessErrorCode =
  | "PROFILE_NOT_FOUND"
  | "EMPLOYEE_INACTIVE"
  | "ACCOUNT_LOCKED"
  | "ACCESS_DENIED"
  | "ENTRA_ACCOUNT_LOCKED"
  | "ENTRA_ACCOUNT_DISABLED"
  | "ENTRA_NOT_ALLOWED"
  | "SERVICE_UNAVAILABLE"
  | "SIGN_IN_FAILED";

export interface AccessError {
  code: AccessErrorCode;
  // Raw code (e.g. AADSTS50057) so HR/IT can find the cause when the user sends a screenshot.
  reference?: string;
}

interface AccessErrorText {
  title: string;
  titleEn: string;
  message: string;
  messageEn: string;
}

export const accessErrorTexts: Record<AccessErrorCode, AccessErrorText> = {
  PROFILE_NOT_FOUND: {
    title: "ไม่พบข้อมูลพนักงาน",
    titleEn: "Employee profile not found",
    message:
      "บัญชีที่ใช้เข้าสู่ระบบไม่มีข้อมูลพนักงานในระบบ HR กรุณาติดต่อฝ่ายบุคคล (HR) เพื่อตรวจสอบข้อมูล หรือลองเข้าสู่ระบบด้วยบัญชีอื่น",
    messageEn:
      "The account you signed in with has no employee record in HR. Please contact HR, or sign in with another account.",
  },
  EMPLOYEE_INACTIVE: {
    title: "บัญชีไม่สามารถใช้งานได้",
    titleEn: "Account no longer active",
    message:
      "สถานะพนักงานของบัญชีนี้ไม่ได้ปฏิบัติงานแล้ว จึงไม่สามารถใช้งาน HR Mobile ได้ หากข้อมูลไม่ถูกต้องกรุณาติดต่อฝ่ายบุคคล (HR)",
    messageEn:
      "This employee is no longer active, so HR Mobile is not available. If this is wrong, please contact HR.",
  },
  ACCOUNT_LOCKED: {
    title: "บัญชีถูกระงับการใช้งาน",
    titleEn: "Account suspended",
    message:
      "บัญชีนี้ถูกระงับการใช้งาน HR Mobile ชั่วคราว กรุณาติดต่อฝ่ายบุคคล (HR) เพื่อสอบถามรายละเอียด",
    messageEn:
      "This account has been suspended from HR Mobile. Please contact HR for details.",
  },
  ACCESS_DENIED: {
    title: "ไม่มีสิทธิ์ดูข้อมูลนี้",
    titleEn: "Access denied",
    message:
      "คุณดูข้อมูลได้เฉพาะของตัวเองเท่านั้น กรุณาออกจากระบบแล้วเข้าสู่ระบบใหม่ด้วยบัญชีของคุณ",
    messageEn:
      "You can only view your own information. Please sign out and sign in again with your own account.",
  },
  ENTRA_ACCOUNT_LOCKED: {
    title: "บัญชีถูกล็อก",
    titleEn: "Account locked",
    message:
      "บัญชี Microsoft ของคุณถูกล็อกชั่วคราว (เช่น ใส่รหัสผ่านผิดหลายครั้ง) กรุณารอสักครู่แล้วลองใหม่ หรือติดต่อ IT Service Desk",
    messageEn:
      "Your Microsoft account is temporarily locked (for example after too many wrong passwords). Wait a while and try again, or contact the IT Service Desk.",
  },
  ENTRA_ACCOUNT_DISABLED: {
    title: "บัญชีถูกปิดการใช้งาน",
    titleEn: "Account disabled",
    message:
      "บัญชี Microsoft ของคุณถูกปิดการใช้งาน กรุณาติดต่อ IT Service Desk",
    messageEn:
      "Your Microsoft account has been disabled. Please contact the IT Service Desk.",
  },
  ENTRA_NOT_ALLOWED: {
    title: "ไม่มีสิทธิ์เข้าใช้งาน",
    titleEn: "Access not allowed",
    message:
      "บัญชีนี้ไม่มีสิทธิ์เข้าใช้งาน HR Mobile กรุณาเข้าสู่ระบบด้วยบัญชีพนักงานของบริษัท หรือติดต่อ IT Service Desk",
    messageEn:
      "This account is not allowed to use HR Mobile. Sign in with your company account, or contact the IT Service Desk.",
  },
  SERVICE_UNAVAILABLE: {
    title: "ระบบไม่สามารถใช้งานได้ชั่วคราว",
    titleEn: "Service temporarily unavailable",
    message:
      "ไม่สามารถโหลดข้อมูลพนักงานได้ในขณะนี้ กรุณาลองใหม่อีกครั้งในอีกสักครู่",
    messageEn:
      "We cannot load your employee profile right now. Please try again in a moment.",
  },
  SIGN_IN_FAILED: {
    title: "เข้าสู่ระบบไม่สำเร็จ",
    titleEn: "Sign-in failed",
    message:
      "เกิดข้อผิดพลาดระหว่างเข้าสู่ระบบ กรุณาลองเข้าสู่ระบบอีกครั้ง หากยังพบปัญหากรุณาติดต่อ IT Service Desk",
    messageEn:
      "Something went wrong while signing in. Please sign in again. If it keeps happening, contact the IT Service Desk.",
  },
};

const apiCodes: AccessErrorCode[] = [
  "PROFILE_NOT_FOUND",
  "EMPLOYEE_INACTIVE",
  "ACCOUNT_LOCKED",
  "ACCESS_DENIED",
];

// Entra ID error codes: https://learn.microsoft.com/entra/identity-platform/reference-error-codes
const entraCodes: Record<string, AccessErrorCode> = {
  AADSTS50053: "ENTRA_ACCOUNT_LOCKED",
  AADSTS50057: "ENTRA_ACCOUNT_DISABLED",
  AADSTS50034: "ENTRA_NOT_ALLOWED",
  AADSTS50020: "ENTRA_NOT_ALLOWED",
  AADSTS50105: "ENTRA_NOT_ALLOWED",
  AADSTS53003: "ENTRA_NOT_ALLOWED",
};

/** Error from GET TimeInOut/GetUserInfo (axios). Returns null for errors that should not block the page. */
export function accessErrorFromApi(error: any): AccessError | null {
  const status = error?.response?.status;
  const data = error?.response?.data;
  // The API sends the code at the top level; older versions only inside detail.
  const code = data?.code ?? data?.detail?.Code;

  if (apiCodes.includes(code)) return { code, reference: code };
  // getInfoUsers rethrows 401 as Error("Unauthorized"); token expiry is handled by the MSAL flow.
  if (error?.message === "Unauthorized") return null;
  // Older API versions return 400 without a code when the profile is not found; other 400s are not about the profile.
  if (status === 400 && isProfileNotFoundMessage(data))
    return { code: "PROFILE_NOT_FOUND", reference: "400" };
  if (!status || status >= 500)
    return {
      code: "SERVICE_UNAVAILABLE",
      reference: status ? `${status}` : "network",
    };
  return null;
}

// The message GetUserInfo sends when the employee is not found, in English or Thai.
function isProfileNotFoundMessage(data: any): boolean {
  const text = JSON.stringify(data ?? "");
  return text.includes("Incorrect User Info") || text.includes("ข้อมูลผู้ใช้ไม่ถูกต้อง");
}

/** Network errors and 5xx may pass on their own, so they are worth one more try before showing an error page. */
export function isTransientApiError(error: any): boolean {
  return accessErrorFromApi(error)?.code === "SERVICE_UNAVAILABLE";
}

/** Error from MSAL (loginRedirect / acquireTokenSilent). */
export function accessErrorFromMsal(error: any): AccessError {
  const text = `${error?.errorMessage ?? ""} ${error?.message ?? ""}`;
  const aadsts = text.match(/AADSTS\d+/)?.[0];
  if (aadsts && entraCodes[aadsts])
    return { code: entraCodes[aadsts], reference: aadsts };
  return { code: "SIGN_IN_FAILED", reference: aadsts ?? error?.errorCode };
}

// Login clears localStorage, so the MSAL error from the redirect is kept in sessionStorage.
const loginErrorKey = "login_access_error";

export function saveLoginError(error: AccessError) {
  sessionStorage.setItem(loginErrorKey, JSON.stringify(error));
}

export function readLoginError(): AccessError | null {
  try {
    const value = sessionStorage.getItem(loginErrorKey);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

export function clearLoginError() {
  sessionStorage.removeItem(loginErrorKey);
}
